import SearchableSelect from './search/SearchableSelect';
import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, TextField, Button, MenuItem, FormControl, InputLabel, Alert } from '@mui/material';
import { updateTransaction, getAccounts, getCategories } from '../services/api';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 400, md: 500 },
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: { xs: 2, sm: 3, md: 4 },
  maxHeight: '90vh',
  overflowY: 'auto'
};

function EditTransactionModal({ open, handleClose, transaction, onTransactionUpdated }) {
  const [gift, setGift] = useState(false);
  const [transactionType, setTransactionType] = useState('expense'); // Add this line
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [transactionDate, setTransactionDate] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (transaction) {
      setGift(transaction.user_label === 'gift');
      setAmount(Math.abs(transaction.amount)); // Always show positive amount
      setDescription(transaction.description);
      setTransactionDate(transaction.transaction_date);
      setSelectedAccount(transaction.account_id);
      setSelectedCategory(transaction.category_id);
      setTransactionType(transaction.amount >= 0 ? 'income' : 'expense'); // ADD THIS LINE
    }
    const fetchAccountsAndCategories = async () => {
      try {
        const accountsRes = await getAccounts();
        setAccounts(accountsRes.data);
        const categoriesRes = await getCategories();
        setCategories(categoriesRes.data);
      } catch (err) {
        console.error("Failed to fetch accounts or categories", err);
        setError("Failed to load accounts or categories.");
      }
    };
    if (open) {
      fetchAccountsAndCategories();
    }
  }, [open, transaction]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    try {
      const updatedData = {
        account_id: selectedAccount,
        category_id: selectedCategory || null,
        amount: transactionType === 'expense' ? -Math.abs(parseFloat(amount)) : Math.abs(parseFloat(amount)),
        transaction_date: transactionDate,
        description: description,
        user_label: gift && transactionType === 'expense' ? 'gift' : null,
      };
      // Preserve system-only natures (transfer/adjustment). Manual, legacy,
      // income, and expense rows can be deliberately classified by the user.
      const labelEditWithSameDirection = (gift || transaction.user_label === 'gift') &&
        transactionType === (Number(transaction.amount) >= 0 ? 'income' : 'expense');
      if ((!transaction.transaction_nature || ['income', 'expense'].includes(transaction.transaction_nature)) && !labelEditWithSameDirection) {
        updatedData.transaction_nature = transactionType;
      }
      await updateTransaction(transaction.id, updatedData);
      onTransactionUpdated();
      handleClose();
    } catch (err) {
      console.error("Failed to update transaction", err);
      setError(err.response?.data?.msg || 'Failed to update transaction');
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style} component="form" onSubmit={handleSubmit}>
        <Typography id="modal-modal-title" variant="h6" component="h2">
          Edit Transaction
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{error}</Alert>}
        <FormControl fullWidth margin="normal" required>
          <InputLabel id="account-select-label">Account</InputLabel>
          <SearchableSelect
            labelId="account-select-label"
            id="account-select"
            value={selectedAccount}
            label="Account"
            onChange={(e) => {
              setSelectedAccount(e.target.value);
              setSelectedCategory('');
            }}
          >
            {accounts.map((account) => (
              <MenuItem key={account.id} value={account.id}>
                {account.name}
              </MenuItem>
            ))}
          </SearchableSelect>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel id="category-select-label">Category</InputLabel>
          <SearchableSelect
            labelId="category-select-label"
            id="category-select"
            value={selectedCategory}
            label="Category"
            onChange={(e) => setSelectedCategory(e.target.value)}
            disabled={!selectedAccount}
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {categories.filter(cat => cat.account_id === selectedAccount).map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </SearchableSelect>
        </FormControl>
        {/* ADD THIS NEW SECTION - Income/Expense Toggle */}
<Box sx={{ mb: 0.5 }}>
  <Box sx={{ display: 'flex', gap: 1 }}>
    <Button
      variant={transactionType === 'income' ? 'contained' : 'outlined'}
      color="success"
      onClick={() => { setTransactionType('income'); setGift(false); }}
      sx={{ 
        flex: 1, 
        py: 0.5,
        minHeight: 44,
        fontSize: '0.75rem'
      }}
      startIcon={<span>💰</span>}
    >
      Income
    </Button>
    <Button
      variant={transactionType === 'expense' && !gift ? 'contained' : 'outlined'}
      color="error"
      onClick={() => { setTransactionType('expense'); setGift(false); }}
      sx={{ 
        flex: 1, 
        py: 0.5,
        minHeight: 44,
        fontSize: '0.75rem'
      }}
      startIcon={<span>💸</span>}
    >
      Expense
    </Button>
    {(!transaction?.transaction_nature || ['income', 'expense'].includes(transaction.transaction_nature)) && <Button
      variant={gift ? 'contained' : 'outlined'} onClick={() => { setTransactionType('expense'); setGift(true); }} sx={{ flex: 1, minHeight: 44 }}>Gift</Button>}
  </Box>
</Box>

{/* MODIFY EXISTING AMOUNT TEXTFIELD */}
<TextField
  margin="normal"
  required
  fullWidth
  label={transactionType === 'expense' ? (gift ? "Gift Amount" : "Amount Spent") : "Income Amount"}
  type="number"
  value={amount}
  onChange={(e) => setAmount(e.target.value)}
  helperText={transactionType === 'expense' ? 
    "Enter positive amount (app will mark as expense)" : 
    "Enter positive amount"
  }
  inputProps={{ min: 0, step: 0.01 }}
  sx={{ 
    '& .MuiInputBase-input': { 
      fontSize: { xs: '16px', md: '14px' }
    }
  }}
/>
        <TextField
          margin="normal"
          required
          fullWidth
          label="Transaction Date"
          type="date"
          InputLabelProps={{
            shrink: true,
          }}
          value={transactionDate}
          onChange={(e) => setTransactionDate(e.target.value)}
          sx={{ 
            '& .MuiInputBase-input': { 
              fontSize: { xs: '16px', md: '14px' }
            }
          }}
        />
        <TextField
          margin="normal"
          fullWidth
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ 
            '& .MuiInputBase-input': { 
              fontSize: { xs: '16px', md: '14px' }
            }
          }}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
        >
          Update Transaction
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={handleClose}
        >
          Cancel
        </Button>
      </Box>
    </Modal>
  );
}

export default EditTransactionModal;
